import { z } from "zod";

/**
 * Studio — Nikki's in-app channel to the assistant, plus Will's approval
 * board. One thread per tenant (single-tenant CRM), no thread ids. Mirrors
 * docs/STUDIO_CONTRACT.md exactly: MessageOut/AttachmentOut/AskOut shapes,
 * the /studio endpoints, and the site-page list for the feedback page picker.
 */

export const StudioAuthorSchema = z.enum(["owner", "admin", "assistant"]);
export type StudioAuthor = z.infer<typeof StudioAuthorSchema>;

export const StudioKindSchema = z.enum(["chat", "feedback", "approval", "system"]);
export type StudioKind = z.infer<typeof StudioKindSchema>;

export const StudioAskStatusSchema = z.enum([
  "received",
  "in_progress",
  "held_for_will",
  "shipped",
  "declined",
]);
export type StudioAskStatus = z.infer<typeof StudioAskStatusSchema>;

/** Board column order for the asks board (contract's grouping order). */
export const ASK_STATUSES: StudioAskStatus[] = [
  "held_for_will",
  "in_progress",
  "received",
  "shipped",
  "declined",
];

export const ASK_STATUS_LABELS: Record<StudioAskStatus, string> = {
  received: "Received",
  in_progress: "In progress",
  held_for_will: "Held for Will",
  shipped: "Shipped",
  declined: "Declined",
};

export const ASK_STATUS_BADGE_VARIANT: Record<
  StudioAskStatus,
  "default" | "success" | "warning" | "danger" | "info" | "primary"
> = {
  received: "info",
  in_progress: "warning",
  held_for_will: "primary",
  shipped: "success",
  declined: "default",
};

// ---- AttachmentOut -----------------------------------------------------

export const AttachmentOutSchema = z.object({
  id: z.string(),
  filename: z.string(),
  content_type: z.string(),
  size_bytes: z.number(),
  // Absolute same-origin path (`/api/v2/studio/attachments/{id}`) — the
  // axios client's baseURL "/api" is already baked in, so this opens/serves
  // directly in <img src> / window.open with the session cookie attached.
  url: z.string(),
});
export type AttachmentOut = z.infer<typeof AttachmentOutSchema>;

// ---- MessageOut ---------------------------------------------------------

export const MessageOutSchema = z.object({
  id: z.string(),
  author: StudioAuthorSchema,
  author_user_id: z.string().nullable(),
  kind: StudioKindSchema,
  body: z.string(),
  // Site path like `/the-aerie`; feedback messages only.
  page: z.string().nullable(),
  attachments: z.array(AttachmentOutSchema),
  ask_id: z.string().nullable(),
  processed_at: z.string().nullable(),
  created_at: z.string(),
});
export type MessageOut = z.infer<typeof MessageOutSchema>;

/** GET /studio/messages page: oldest→newest within the page. */
export const MessagesPageSchema = z.object({
  items: z.array(MessageOutSchema),
  next_before: z.string().nullable(),
});
export type MessagesPage = z.infer<typeof MessagesPageSchema>;

// ---- AskOut -------------------------------------------------------------

export const AskOutSchema = z.object({
  id: z.string(),
  title: z.string(),
  detail: z.string().nullable(),
  page: z.string().nullable(),
  status: StudioAskStatusSchema,
  decision: z.string().nullable(),
  source_message_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  resolved_at: z.string().nullable(),
});
export type AskOut = z.infer<typeof AskOutSchema>;

export const AsksPageSchema = z.object({
  items: z.array(AskOutSchema),
});
export type AsksPage = z.infer<typeof AsksPageSchema>;

// ---- SummaryOut ----------------------------------------------------------

/**
 * GET /studio/summary. `asks` maps status → count, but the contract does not
 * promise every status key is present, so the record stays open (string keys)
 * and readers use askCount() for a defaulted lookup.
 */
export const SummaryOutSchema = z.object({
  unprocessed_from_people: z.number(),
  asks: z.record(z.string(), z.number()),
  last_assistant_at: z.string().nullable(),
});
export type SummaryOut = z.infer<typeof SummaryOutSchema>;

export function askCount(summary: SummaryOut | undefined, status: StudioAskStatus): number {
  return summary?.asks[status] ?? 0;
}

// ---- Site pages for the page picker ---------------------------------------
// From thefoxandtheowl.net/sitemap.xml (contract's page list).

export interface StudioPageRef {
  path: string;
  label: string;
}

export const PAGES: readonly StudioPageRef[] = [
  { path: "/", label: "Home" },
  { path: "/founding", label: "Founding" },
  { path: "/the-aerie", label: "The Aerie" },
  { path: "/the-wilder", label: "The Wilder" },
  { path: "/event-space", label: "Event Space" },
  { path: "/weddings", label: "Weddings" },
  { path: "/the-nest", label: "The Nest" },
  { path: "/clubs", label: "Clubs" },
  { path: "/parties", label: "Parties" },
  { path: "/meet-nicole", label: "Meet Nicole" },
  { path: "/faq", label: "FAQ" },
  { path: "/party-room-rental-bulverde-tx", label: "Party Room Rental" },
  { path: "/meeting-room-rental-bulverde-tx", label: "Meeting Room Rental" },
  { path: "/practitioners", label: "Practitioners" },
  { path: "/wellness-room-rental-bulverde", label: "Wellness Room Rental" },
  { path: "/art-studio-membership-north-san-antonio", label: "Art Studio Membership" },
  { path: "/homeschool-art-nature-classes-bulverde", label: "Homeschool Art & Nature Classes" },
  { path: "/homeschool-co-op-meeting-space-bulverde", label: "Homeschool Co-op Meeting Space" },
  { path: "/art-birthday-party-bulverde-stone-oak", label: "Art Birthday Party" },
  { path: "/things-to-do-bulverde-tx", label: "Things to Do in Bulverde" },
  { path: "/wedding-coordinator-bulverde-tx", label: "Wedding Coordinator" },
  { path: "/small-wedding-venue-hill-country", label: "Small Wedding Venue" },
  { path: "/baby-bridal-shower-venue-bulverde", label: "Baby & Bridal Shower Venue" },
  { path: "/yoga-space-rental-hourly-san-antonio-north", label: "Yoga Space Rental" },
  { path: "/is-the-nest-childcare-homeschool-classes", label: "The Nest Childcare & Classes" },
];

/** Human label for a site path; falls back to the raw path if unknown. */
export function pageLabel(path: string | null | undefined): string {
  if (!path) return "";
  return PAGES.find((p) => p.path === path)?.label ?? path;
}
